import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';

import LoadStatus from 'config/load_status';

import createAccount from 'domain/authentication/usecases/createAccount';
import ValidationError from 'domain/errors/ValidationError';

import delayed from 'utils/delayed';

const useCreateAccountRequest = (signUpForm) => {
  const { enqueueSnackbar } = useSnackbar();
  const [createAccountStatus, setCreateAccountStatus] = useState(
    LoadStatus.LOADING
  );
  const [isFormInvalid, setIsFormInvalid] = useState(false);

  useEffect(() => {
    if (createAccountStatus !== LoadStatus.LOADING) return;

    const postData = async () => {
      try {
        await delayed(createAccount(signUpForm));

        setCreateAccountStatus(LoadStatus.DONE);
        enqueueSnackbar('Account was created successfully', {
          variant: 'success',
        });
      } catch (error) {
        setCreateAccountStatus(LoadStatus.ERROR);

        if (error instanceof ValidationError) {
          setIsFormInvalid(true);
          return;
        }

        enqueueSnackbar('Account creation failed', { variant: 'error' });
        throw error;
      }
    };
    postData();
  }, [createAccountStatus, signUpForm, enqueueSnackbar]);

  const tryAgain = () => {
    if (createAccountStatus === LoadStatus.LOADING) return;
    if (createAccountStatus === LoadStatus.DONE) return;

    setCreateAccountStatus(LoadStatus.LOADING);
  };

  return {
    isLoading: createAccountStatus === LoadStatus.LOADING,
    hasFailed: createAccountStatus === LoadStatus.ERROR && !isFormInvalid,
    isFormInvalid,
    tryAgain,
    hasFinished: createAccountStatus === LoadStatus.DONE,
  };
};

export default useCreateAccountRequest;
